import { AnalyticsEvents } from "../constants/events";
import { shouldSkipDuplicate } from "../core/dedupe";
import { sanitizeAnalyticsParams } from "../core/sanitizeParams";
import { baseDeviceParams } from "../core/deviceParams";
import {
  initFirebaseAnalytics,
  isAnalyticsInitialized,
  sendAnalyticsEvent,
} from "../core/analyticsClient";

let bootstrapPromise = null;

export async function ensureAnalyticsInitialized() {
  if (isAnalyticsInitialized()) return true;
  if (!bootstrapPromise) {
    bootstrapPromise = initFirebaseAnalytics().finally(() => {
      bootstrapPromise = null;
    });
  }
  return bootstrapPromise;
}

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} [params]
 * @param {{ dedupe?: boolean }} [options]
 */
export function trackEvent(eventName, params = {}, options = {}) {
  const merged = {
    ...baseDeviceParams(),
    ...params,
  };
  const safe = sanitizeAnalyticsParams(merged);
  const useDedupe = options.dedupe !== false;
  if (useDedupe && shouldSkipDuplicate(eventName, safe)) return;

  void ensureAnalyticsInitialized().then((ready) => {
    if (!ready) return;
    sendAnalyticsEvent(eventName, safe);
  });
}

export function trackAppOpen() {
  trackEvent(AnalyticsEvents.APP_OPEN, {}, { dedupe: true });
}

export function trackPwaInstall() {
  trackEvent(AnalyticsEvents.PWA_INSTALL);
}

export function trackAppUpdate(extra = {}) {
  trackEvent(AnalyticsEvents.APP_UPDATE, extra);
}

export function trackOfflineEntered(extra = {}) {
  trackEvent(AnalyticsEvents.OFFLINE_MODE_ENTERED, extra);
}

export function trackOfflineExited(extra = {}) {
  trackEvent(AnalyticsEvents.OFFLINE_MODE_EXITED, extra);
}

export function trackMatchCreated(ctx = {}) {
  trackEvent(AnalyticsEvents.MATCH_CREATED, ctx);
}

export function trackMatchJoined(ctx = {}) {
  trackEvent(AnalyticsEvents.MATCH_JOINED, ctx);
}

export function trackMatchStarted(ctx = {}) {
  trackEvent(AnalyticsEvents.MATCH_STARTED, ctx);
}

export function trackMatchCompleted(ctx = {}) {
  trackEvent(AnalyticsEvents.MATCH_COMPLETED, ctx);
}

export function trackMatchAbandoned(ctx = {}) {
  trackEvent(AnalyticsEvents.MATCH_ABANDONED, ctx);
}

export function trackShareLink(extra = {}) {
  trackEvent(AnalyticsEvents.SHARE_LINK, extra);
}

export function trackShareQr(extra = {}) {
  trackEvent(AnalyticsEvents.SHARE_QR, extra);
}

export function trackShareNative(extra = {}) {
  trackEvent(AnalyticsEvents.SHARE_NATIVE, extra);
}

export function trackSpectatorOpened(extra = {}) {
  trackEvent(AnalyticsEvents.SPECTATOR_OPENED, extra);
}

export function trackUndoUsed(extra = {}) {
  trackEvent(AnalyticsEvents.UNDO_USED, extra);
}

export function trackIntervalStarted(extra = {}) {
  trackEvent(AnalyticsEvents.INTERVAL_STARTED, extra);
}

export function trackGameCompleted(extra = {}) {
  trackEvent(AnalyticsEvents.GAME_COMPLETED, extra);
}

export function trackMatchPoint(extra = {}) {
  trackEvent(AnalyticsEvents.MATCH_POINT, extra);
}

export function trackGamePoint(extra = {}) {
  trackEvent(AnalyticsEvents.GAME_POINT, extra);
}

export function trackCourtSetupCompleted(extra = {}) {
  trackEvent(AnalyticsEvents.COURT_SETUP_COMPLETED, extra);
}

export function trackCourtSwapped(extra = {}) {
  trackEvent(AnalyticsEvents.COURT_SWAPPED, extra);
}

export function trackInstallPromptShown() {
  trackEvent(AnalyticsEvents.INSTALL_PROMPT_SHOWN);
}

export function trackInstallPromptDismissed() {
  trackEvent(AnalyticsEvents.INSTALL_PROMPT_DISMISSED);
}

export function trackInstallCompleted() {
  trackEvent(AnalyticsEvents.INSTALL_COMPLETED);
}

export function trackUpdateAvailable(extra = {}) {
  trackEvent(AnalyticsEvents.UPDATE_AVAILABLE, extra);
}

export function trackUpdateInstalled(extra = {}) {
  trackEvent(AnalyticsEvents.UPDATE_INSTALLED, extra);
}

export function trackFailedRoomJoin(extra = {}) {
  trackEvent(AnalyticsEvents.FAILED_ROOM_JOIN, extra, { dedupe: false });
}

export function trackSyncConflict(extra = {}) {
  trackEvent(AnalyticsEvents.SYNC_CONFLICT, extra, { dedupe: false });
}

export function trackFirestorePermissionDenied(extra = {}) {
  trackEvent(AnalyticsEvents.FIRESTORE_PERMISSION_DENIED, extra, {
    dedupe: false,
  });
}

export function trackOfflineSyncFailed(extra = {}) {
  trackEvent(AnalyticsEvents.OFFLINE_SYNC_FAILED, extra, { dedupe: false });
}

export { AnalyticsEvents };
